import {
    GroupByOption,
    SignalsEndpoints,
} from "components/Signals/SignalsModels";
import type {
    ISignalsSelectionTypes,
    SignalDataEndpoint,
} from "components/Signals/SignalsModels";
import { Palettes } from "components/UI/Visualization/GraphColorSchemes";
import { stripGroupingWhenNotComparePrevOrGrouping } from "./Transforms";
import {
    EndpointBehaviorConfigMap,
    mergeWithDefaults,
} from "./DefaultConfigValues";
import type { EndpointBehaviorConfig, KboModelOption } from "./Types";
import {
    buildAverageScoreByModuleOverTimeGrid,
    buildAverageScoreByQuestionOverTimeGrid,
    buildClassifierOverTimeGrid,
    buildContactTypesOverTimeGrid,
    buildEddyByClassifierOverTimeGrid,
    buildEddyByContactTypeOverTimeGrid,
    buildHipaaOverTimeGrid,
    buildQuestionResponseOverTimeGrid,
    buildSafetyEventsOverTimeGrid,
    buildTopicPrevalenceGrid,
    buildModelPerformanceGrid,
} from "./DataGridBuilders";
import { drilldownStrategies } from "./DrilldownStrategies";
import ClassifierService from "services/ClassifierService";
import { SlimLLMClassifierResponse } from "models/LLMs/LLMClassifier";
import { SkillGrouping } from "./Types";
import {
    driversDataAvailabilityPolicy,
    topicsDataAvailabilityPolicy,
} from "components/Signals/DataAvailability/DataAvailability";

const {
    eddyReports,
    trends,
    legacyTopicReports,
    drivers,
    themes,
    contacts,
    additionalReports,
    smallCards,
} = SignalsEndpoints;

const classifierService = new ClassifierService();

// Pulls the classifierGroupId out of a published grouped chart's endpoint
// (e.g. "PublishedGroupedModelResults?classifierGroupId=<guid>").
const parseClassifierGroupId = (dataEndpoint?: string): string | undefined => {
    if (!dataEndpoint?.includes("?")) return undefined;
    return (
        new URLSearchParams(dataEndpoint.split("?")[1]).get(
            "classifierGroupId",
        ) ?? undefined
    );
};

// The deepest (last) id of a classifier-group selection path is the chart's scope; accepts either the
// array path or a bare id.
const deepestId = (path?: string | string[]): string | undefined =>
    Array.isArray(path) ? path[path.length - 1] : path;

// Shared loader for grouped model-results charts: the chart's classifier group's members (rolled up
// across sub-groups to match the chart). `analyzable` reflects the classifier's TYPE (only LLM models
// have reasoning KBo can analyze) — Lucene/RBC members are kept but disabled by the card. Results (for
// the result dropdown) come from the org's published LLM classifiers when available. Whether a model
// actually has chart data is decided at render time from the chart data, not here. Group order (by
// name) is preserved.
const loadGroupClassifierOptions = async ({
    dataEndpoint,
    classifierGroup,
    llmClassifiers,
}: {
    dataEndpoint?: string;
    classifierGroup?: string | string[];
    llmClassifiers: SlimLLMClassifierResponse[];
}): Promise<KboModelOption[]> => {
    const groupId =
        parseClassifierGroupId(dataEndpoint) ?? deepestId(classifierGroup);
    if (!groupId) return [];
    const inGroup = await classifierService.getClassifiersInGroup(
        groupId,
        undefined,
        true,
    );
    const byId = new Map(llmClassifiers.map((c) => [c.id, c]));
    return inGroup.map((c) => {
        const llm = byId.get(c.id);
        return {
            id: c.id,
            name: c.name,
            results: llm?.results ?? [],
            // A model has reasoning to analyze when it's an LLM (non-extraction) classifier. Trust the
            // backend's type flag, and also treat any published LLM already in the org options as
            // analyzable so the card keeps working if that flag is absent (older backend).
            analyzable: c.isLlm === true || !!llm,
        };
    });
};

const normalizeSelectionIds = (ids?: Array<string | number>): string[] =>
    (ids ?? []).map((id) => `${id}`);

const normalizeSelectedIdsValue = (value?: string | string[]): string[] =>
    Array.isArray(value)
        ? value.map((id) => `${id}`)
        : value
        ? [`${value}`]
        : [];

const haveSameSelections = (left: string[], right: string[]): boolean => {
    if (left.length !== right.length) return false;
    const leftSet = new Set(left);
    return right.every((id) => leftSet.has(id));
};

const getSelectedSeriesLabels = (
    selectedRows: Array<Record<string, unknown>>,
    fallbackIds: string[],
    fields: string[],
): string[] => {
    const labels = selectedRows
        .map((row) => {
            for (const field of fields) {
                const value = row[field];
                if (
                    value !== undefined &&
                    value !== null &&
                    `${value}`.length
                ) {
                    return `${value}`;
                }
            }
            const rowId = row.id;
            return rowId !== undefined && rowId !== null ? `${rowId}` : "";
        })
        .filter((value) => value.length > 0);

    return labels.length ? labels : fallbackIds;
};

/**
 * Per-endpoint overrides. Anything omitted falls back to `mergeWithDefaults`.
 *
 * `pendo.addCard` follows one convention: `add-card-` plus the report's Add Cards title in
 * kebab-case ("Top 20 Themes" -> `add-card-top-20-themes`). Two deviations are deliberate and
 * should not be "corrected" to match their titles:
 *   - `ThemesAndTopDrivers` is `add-card-themes-and-call-drivers`, the tag AUT-9170 specifies.
 *     Product wrote it while the report was still named Themes & Call Drivers, and Pendo is
 *     configured against that string.
 *   - The three deprecated Topic reports share their titles with the Driver-backed Theme reports
 *     that replaced them, so they carry a `-deprecated` suffix. DOM ids must be unique, and
 *     product needs to tell "added the new card" from "added the deprecated one" apart.
 *
 * The three auto-generated Published* endpoints below declare no id: `availableCharts` filters
 * them out of the palette, so they can never render a tile to tag.
 */
/**
 * What an insight (small) card offers when the card is clicked. Shared rather than repeated per
 * card because the three actions are a property of the surface, not of any one stat.
 */
const SMALL_CARD_POINT_SELECT_ACTIONS = {
    viewData: true,
    viewInConvos: true,
    askKbo: true,
    viewTopics: false,
    viewDrivers: false,
    viewLevel: false,
};

const endpointBehaviorConfigs: EndpointBehaviorConfigMap = {
    // Insight (small) cards. These render a single stat rather than a plot, so most of the chart
    // config is inert for them; what they declare here is the point-select menu the whole card
    // opens. View Topics / View Drivers / View {level} never apply -- there is no breakdown behind
    // a stat to open -- so they are turned off rather than inherited from the defaults.
    [smallCards.TotalConversationsId]: {
        pointSelectActions: SMALL_CARD_POINT_SELECT_ACTIONS,
    },
    [smallCards.AvgCallDurationId]: {
        pointSelectActions: SMALL_CARD_POINT_SELECT_ACTIONS,
        kboContextSuffix: () =>
            "Note: this card covers audio conversations only.",
    },
    [smallCards.ConversationsWithAnEddyId]: {
        pointSelectActions: SMALL_CARD_POINT_SELECT_ACTIONS,
        kboContextSuffix: () =>
            "Note: this data point only includes conversations in which an Eddy was identified.",
    },
    [smallCards.AvgModuleScoreId]: {
        pointSelectActions: SMALL_CARD_POINT_SELECT_ACTIONS,
        kboContextSuffix: () =>
            "Note: this data point only includes evaluated conversations scored on the selected module category.",
    },

    // Eddy Reports
    [eddyReports.AverageScoreByQuestionID]: {
        pendo: {
            addCard: "add-card-average-score-by-question-and-eddy",
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: true,
        },
        renderOptions: {
            // % is an average score, not a share of conversations
            hideEligibleConversations: true,
            palette: Palettes.specialEddyColors,
        },
        capabilities: {
            renderModes: ["%"],
        },
        pagination: {
            paginated: true,
        },
        kboContextSuffix: ({ x }) => {
            const q = x ? ` to the question "${x}"` : "";
            return `Note: this data point only includes conversations with a scored response${q}.`;
        },
        kboUseYPercentTotal: true,
    },
    [eddyReports.AverageScoreByQuestionAndEddyOverTimeID]: {
        pendo: {
            addCard: "add-card-average-score-by-question-and-eddy-over-time",
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: true,
        },
        renderOptions: {
            // % is an average score, not a share of conversations
            hideEligibleConversations: true,
            legendVisibility: "bottom",
            palette: Palettes.specialEddyColors,
            isTemporal: true,
        },
        capabilities: {
            renderModes: ["%"],
        },
        kboContextSuffix: () =>
            "Note: this data point only includes conversations with scored responses for the selected question.",
        kboUseYPercentTotal: true,
        requestPatch: (req) => {
            const questionIds = Array.isArray(req.questionIds)
                ? req.questionIds.filter(
                      (id): id is string => typeof id === "string",
                  )
                : [];
            const questionId =
                typeof req.questionId === "string" ? req.questionId : undefined;
            if (questionIds.length === 0 && questionId) {
                const { questionId: _questionId, ...rest } = req;
                return {
                    ...rest,
                    questionIds: [questionId],
                };
            }
            return req;
        },
    },
    [eddyReports.ClassifierAndEddyID]: {
        pendo: {
            addCard: "add-card-classifiers-and-eddy-effect",
        },
        renderOptions: {
            palette: Palettes.specialEddyColors,
            forceStacked: true,
            cacheKeyStrategy: "filtersChange",
        },
        capabilities: {
            renderModes: ["#"],
        },
        pagination: {
            paginated: true,
        },
    },
    [eddyReports.EEByClassifierID]: {
        pendo: {
            addCard: "add-card-eddy-effect-by-classifier",
        },
        renderOptions: {
            palette: Palettes.specialEddyColors,
            comparePrevPalette: Palettes.specialEddyComparePrev,
            preferSeriesInTooltip: true,
            cacheKeyStrategy: "filtersChange",
        },
        capabilities: {
            comparePrev: true,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [
                GroupByOption.EddyEffect,
                GroupByOption.Classifiers,
            ],
        },
        pagination: {
            paginated: true,
        },
    },
    [eddyReports.EEByClassifiersOverTimeID]: {
        pendo: {
            addCard: "add-card-eddy-effect-by-classifiers-over-time",
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
            cacheKeyStrategy: "filtersChange",
        },
        pagination: {
            paginated: true,
        },
        dataGrid: {
            gridColumnType: "Classifier With Eddy",
            dataGridBuilder: buildEddyByClassifierOverTimeGrid,
            followPagination: true,
        },
    },
    [eddyReports.EEOverTimeID]: {
        pendo: {
            addCard: "add-card-eddy-effect-over-time",
        },
        renderOptions: {
            palette: Palettes.specialEddyColors,
            isTemporal: true,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.EddyEffect],
        },
    },
    [eddyReports.EEPrevalenceByQuestionID]: {
        pendo: {
            addCard: "add-card-eddy-effect-by-question-response",
        },
        renderOptions: {
            palette: Palettes.specialEddyColors,
            comparePrevPalette: Palettes.specialEddyComparePrev,
        },
        capabilities: {
            comparePrev: true,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.EddyEffect],
        },
        pagination: {
            paginated: true,
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: false,
        },
    },
    [eddyReports.EEPrevalenceByQuestionOverTimeID]: {
        pendo: {
            addCard: "add-card-eddy-effect-by-question-response-over-time",
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: false,
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
        },
        dataGrid: {
            gridColumnType: "Question Response",
            dataGridBuilder: buildQuestionResponseOverTimeGrid,
            selection: {
                behavior: "noRequest",
                onSelect: ({ chartStore, dataGridStore }) => {
                    const selectedIds = normalizeSelectionIds(
                        dataGridStore.selectedRowIds as Array<string | number>,
                    );
                    const current = normalizeSelectedIdsValue(
                        chartStore.selectedIds.tags as string[] | undefined,
                    );
                    if (!haveSameSelections(selectedIds, current)) {
                        chartStore.setSelectedId("tags", selectedIds);
                        chartStore.handleSelectionChange({
                            skipDataGridRefresh: true,
                        });
                    }
                    return getSelectedSeriesLabels(
                        dataGridStore.selectedRows as Array<
                            Record<string, unknown>
                        >,
                        selectedIds,
                        ["value", "displayName"],
                    );
                },
            },
        },
    },

    // Trends
    [trends.QualityScoreOverTimeID]: {
        pendo: {
            addCard: "add-card-quality-score-over-time",
        },
        renderOptions: {
            // % is an average score, not a share of conversations
            hideEligibleConversations: true,
            isTemporal: true,
        },
        capabilities: {
            renderModes: ["%"],
            spotlight: false,
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: true,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.Classifiers],
        },
        kboContextSuffix: () =>
            "Note: this data point only includes conversations with a quality scoring module score.",
        kboUseYPercentTotal: true,
    },
    [trends.AverageScoreBySkillID]: {
        pendo: {
            addCard: "add-card-average-score-by-skill",
        },
        capabilities: {
            // Percent only: the AC excludes the #/% toggle.
            renderModes: ["%"],
        },
        // The endpoint returns every scored question in the module; pagination keeps
        // CHART_PAGE_SIZE bars on screen while leaving the rest reachable.
        pagination: {
            paginated: true,
        },
        // Quality modules only, and only worth showing when there is a choice to make.
        moduleSelector: {
            categories: ["Quality"],
            hideWhenSingleOption: true,
        },
        // "Skill groups" are questions with the "Question Group" answer type; each group's score is
        // the average of its child questions' scores.
        skillGroupingSelector: {
            options: [
                { label: "Individual Skills", value: SkillGrouping.Individual },
                { label: "Skill Groups", value: SkillGrouping.Group },
            ],
            defaultValue: SkillGrouping.Individual,
        },
        renderOptions: {
            // % is an average score, not a share of conversations
            hideEligibleConversations: true,
            // Ungrouped this is a single series, so "auto" leaves the legend off
            // rather than repeating the title. Grouping splits it into one series
            // per group-by value, and "auto" lets that legend through -- "none"
            // would suppress it and leave the slice colors unlabeled.
            legendVisibility: "auto",
            // Show a clearer hover title than the shorter label used on the x-axis.
            tooltipTitleMetadataKey: "FullQuestionText",
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: true,
        },
        grouping: {
            enabled: true,
        },
        kboContextSuffix: ({ x, skillGrouping }) => {
            if (skillGrouping === SkillGrouping.Group) {
                const group = x ? ` "${x}"` : "";
                return `Note: this data point is the skill group${group}, whose score is the average of its child questions' scores. It only includes conversations with a scored response for that skill group.`;
            }
            const skill = x ? ` to the question "${x}"` : "";
            return `Note: this data point only includes conversations with a scored response${skill}.`;
        },
        kboUseYPercentTotal: true,
    },
    [trends.AverageScoreByModuleOverTimeID]: {
        pendo: {
            addCard: "add-card-average-score-by-module-over-time",
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: true,
        },
        renderOptions: {
            // % is an average score, not a share of conversations
            hideEligibleConversations: true,
            legendVisibility: "bottom",
            isTemporal: true,
            cacheKeyStrategy: "filtersOrSelectionsChange",
        },
        capabilities: {
            renderModes: ["%"],
        },
        kboContextSuffix: ({ series }) =>
            series
                ? `Note: this data point only includes conversations scored on the "${series}" module.`
                : "",
        kboUseYPercentTotal: true,
        dataGrid: {
            gridColumnType: "Module Score",
            dataGridBuilder: buildAverageScoreByModuleOverTimeGrid,
            selection: {
                // The backend only ever returns the modules named by request.ModuleIds
                // (falling back to an arbitrary 7 when none are sent), so the selection
                // has to travel with the request rather than be filtered in locally.
                behavior: "request",
                onSelect: ({ chartStore, dataGridStore }) => {
                    const selectedIds = normalizeSelectionIds(
                        dataGridStore.selectedRowIds as Array<string | number>,
                    );
                    const current = normalizeSelectedIdsValue(
                        chartStore.selectedIds.modules as string[] | undefined,
                    );
                    if (!haveSameSelections(selectedIds, current)) {
                        chartStore.setSelectedId("modules", selectedIds);
                        chartStore.handleSelectionChange({
                            skipDataGridRefresh: true,
                        });
                    }
                    return getSelectedSeriesLabels(
                        dataGridStore.selectedRows as Array<
                            Record<string, unknown>
                        >,
                        selectedIds,
                        ["name", "displayName"],
                    );
                },
            },
        },
    },
    [trends.AverageScoreByQuestionOverTimeID]: {
        pendo: {
            addCard: "add-card-average-score-by-question-over-time",
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: true,
        },
        renderOptions: {
            // % is an average score, not a share of conversations
            hideEligibleConversations: true,
            legendVisibility: "bottom",
            isTemporal: true,
            cacheKeyStrategy: "filtersOrSelectionsChange",
        },
        capabilities: {
            renderModes: ["%"],
        },
        kboContextSuffix: ({ series }) =>
            series
                ? `Note: this data point only includes conversations with a scored response to the question: "${series}".`
                : "",
        kboUseYPercentTotal: true,
        dataGrid: {
            gridColumnType: "Question",
            dataGridBuilder: buildAverageScoreByQuestionOverTimeGrid,
            selection: {
                behavior: "request",
                onSelect: ({ chartStore, dataGridStore }) => {
                    const selectedIds = normalizeSelectionIds(
                        dataGridStore.selectedRowIds as Array<string | number>,
                    );
                    const current = normalizeSelectedIdsValue(
                        chartStore.selectedIds.questions as
                            | string[]
                            | undefined,
                    );
                    if (!haveSameSelections(selectedIds, current)) {
                        chartStore.setSelectedId("questions", selectedIds);
                        chartStore.handleSelectionChange({
                            skipDataGridRefresh: true,
                        });
                    }
                    return getSelectedSeriesLabels(
                        dataGridStore.selectedRows as Array<
                            Record<string, unknown>
                        >,
                        selectedIds,
                        ["shortQuestionText", "questionText"],
                    );
                },
            },
        },
    },
    [trends.ClassifierOverTimeId]: {
        pendo: {
            addCard: "add-card-classifiers-over-time",
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
            cacheKeyStrategy: "filtersChange",
        },
        pagination: {
            paginated: true,
        },
        dataGrid: {
            gridColumnType: "Classifier Prevalence",
            dataGridBuilder: buildClassifierOverTimeGrid,
            followPagination: true,
        },
    },
    [trends.HipaaCompliancePrevalenceOverTimeId]: {
        pendo: {
            addCard: "add-card-hipaa-compliance-over-time",
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
        },
        dataGrid: {
            gridColumnType: "Hipaa",
            dataGridBuilder: buildHipaaOverTimeGrid,
            selection: {
                behavior: "noRequest",
                onSelect: ({ dataGridStore }) =>
                    dataGridStore.selectedRowIds.map((rowId) => `${rowId}`),
            },
        },
    },
    [trends.SEPrevalenceOverTimeID]: {
        pendo: {
            addCard: "add-card-safety-events-over-time",
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
        },
        dataGrid: {
            gridColumnType: "Safety Events",
            dataGridBuilder: buildSafetyEventsOverTimeGrid,
            selection: {
                behavior: "noRequest",
                onSelect: ({ dataGridStore }) =>
                    dataGridStore.selectedRowIds.map((rowId) => `${rowId}`),
            },
        },
    },

    // The two Driver Cards. They mirror TrendingTopics and TopicsOnTheRise in layout and
    // capability, but read the resolved Primary Driver rather than an actual Primary Topic.
    //
    // Unlike their deprecated Topic predecessors, Driver cards retain Ask KBo support. The Driver
    // strategies below the config layer apply Driver filters instead of Topic filters.
    [drivers.TrendingDriversID]: {
        pendo: {
            addCard: "add-card-trending-drivers",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        capabilities: {
            renderModes: ["#"],
            spotlight: false,
            kboAnalyze: true,
        },
        renderOptions: {
            menu: true,
            dataGridNumVisibleRows: 10,
        },
        pointSelectActions: {
            askKbo: true,
            viewInConvos: true,
            viewTopics: false,
            viewDrivers: false,
            viewData: true,
            viewLevel: false,
        },
    },
    [drivers.DriversOnTheRiseID]: {
        pendo: {
            addCard: "add-card-drivers-on-the-rise",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        capabilities: {
            pointSelectMenu: true,
        },
        pointSelectActions: {
            askKbo: true,
            viewInConvos: true,
            viewTopics: false,
            viewDrivers: false,
            viewData: true,
            viewLevel: false,
        },
    },

    // Retained Topic reports backed by Fast Reporting Themes.
    [legacyTopicReports.PrimaryThemesId]: {
        // AKA TopicPrevalence
        pendo: {
            addCard: "add-card-primary-themes-deprecated",
        },
        dataAvailability: topicsDataAvailabilityPolicy,
        status: {
            tag: "Deprecated",
            infoText:
                "Drivers replaced Topics on 7/1/26. This report can only display Topics data prior to that date.",
            permission: "Drivers",
        },
        pagination: {
            paginated: true,
        },
        renderOptions: {
            cacheKeyStrategy: "filtersChange",
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.PrimaryThemes],
        },
        pointSelectActions: {
            viewTopics: true,
            askKbo: false,
        },
    },
    // The three Theme Prevalence Cards mirror their TopicPrevalence* predecessors exactly, except
    // that they read the Primary Driver's Theme. They offer no View Topics: Theme* inner-joins
    // through the Primary Driver, so a Topic breakdown could never describe their population.
    [themes.ThemePrevalenceId]: {
        pendo: {
            addCard: "add-card-primary-themes",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        pagination: {
            paginated: true,
        },
        renderOptions: {
            cacheKeyStrategy: "filtersChange",
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.Themes],
        },
        capabilities: {
            spotlight: false,
        },
        // The Driver breakdown, replacing the View Topics its predecessor offers.
        pointSelectActions: {
            viewDrivers: true,
        },
    },
    [themes.ThemePrevalenceAndEEId]: {
        pendo: {
            addCard: "add-card-themes-and-eddy-effect",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        renderOptions: {
            palette: Palettes.specialEddyColors,
            forceStacked: true,
            cacheKeyStrategy: "filtersChange",
        },
        capabilities: {
            spotlight: false,
        },
        pagination: {
            paginated: true,
        },
        pointSelectActions: {
            viewDrivers: true,
        },
    },
    [themes.ThemePrevalenceOverTimeId]: {
        pendo: {
            addCard: "add-card-themes-over-time",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
            cacheKeyStrategy: "filtersChange",
        },
        capabilities: {
            spotlight: false,
        },
        pagination: {
            paginated: true,
        },
        dataGrid: {
            gridColumnType: "Topics",
            dataGridBuilder: buildTopicPrevalenceGrid,
            followPagination: true,
        },
        pointSelectActions: {
            viewDrivers: true,
        },
    },
    [drivers.TopTwentyThemesID]: {
        pendo: {
            addCard: "add-card-top-20-themes",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        capabilities: {
            spotlight: false,
            kboAnalyze: false,
            comparePrev: false,
            pointSelectMenu: true,
            sqlView: true,
            pngExport: false,
            csvExport: "none" as const,
            renderModes: ["#" as const],
            objectFilters: true,
        },
        pointSelectActions: {
            viewData: true,
            viewInConvos: true,
            viewTopics: false,
            askKbo: true,
            viewLevel: true,
        },
        grouping: {
            enabled: false,
            disabledGroupByNames: [],
        },
        renderOptions: {
            menu: true,
            emptyStateVariant: "empty" as const,
            palette: Palettes.treeMapColorScheme,
        },
        pagination: {
            paginated: false,
        },
        // Related secondary/tertiary drivers are informational only. They do
        // not have valid point-action filters, so the deepest zoom level is
        // intentionally non-interactive while the first two levels retain
        // their normal actions.
        pointSelectActionsByZoomLevel: {
            2: {
                askKbo: false,
                viewInConvos: false,
                viewTopics: false,
                viewData: false,
                viewLevel: false,
            },
        },
    },
    [drivers.ThemesAndTopDriversID]: {
        pendo: {
            addCard: "add-card-themes-and-call-drivers",
        },
        dataAvailability: driversDataAvailabilityPolicy,
        capabilities: {
            spotlight: false,
            kboAnalyze: false,
            pointSelectMenu: true,
            objectFilters: true,
        },
        renderOptions: {
            forceStacked: true,
            cacheKeyStrategy: "filtersChange",
            legendVisibility: "none",
        },
        pagination: {
            paginated: true,
        },
        grouping: {
            enabled: false,
            fixedGroupingOrderRule: {
                groupByOption: GroupByOption.PrimaryDriver,
            },
        },
        pointSelectActions: {
            askKbo: true,
            viewInConvos: true,
            viewTopics: false,
            viewData: true,
            viewLevel: false,
        },
        percentRemainderSeries: true,
    },
    [legacyTopicReports.ThemeAndEEID]: {
        pendo: {
            addCard: "add-card-themes-and-eddy-effect-deprecated",
        },
        dataAvailability: topicsDataAvailabilityPolicy,
        status: {
            tag: "Deprecated",
            infoText:
                "Drivers replaced Topics on 7/1/26. This report can only display Topics data prior to that date.",
            permission: "Drivers",
        },
        renderOptions: {
            palette: Palettes.specialEddyColors,
            forceStacked: true,
            cacheKeyStrategy: "filtersChange",
        },
        capabilities: {
            spotlight: true,
        },
        pagination: {
            paginated: true,
        },
        pointSelectActions: {
            askKbo: false,
        },
    },
    [legacyTopicReports.ThemesOverTimeId]: {
        // AKA TopicPrevalenceOverTime
        pendo: {
            addCard: "add-card-themes-over-time-deprecated",
        },
        dataAvailability: topicsDataAvailabilityPolicy,
        status: {
            tag: "Deprecated",
            infoText:
                "Drivers replaced Topics on 7/1/26. This report can only display Topics data prior to that date.",
            permission: "Drivers",
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
            cacheKeyStrategy: "filtersChange",
        },
        pagination: {
            paginated: true,
        },
        dataGrid: {
            gridColumnType: "Topics",
            dataGridBuilder: buildTopicPrevalenceGrid,
            followPagination: true,
        },
        pointSelectActions: {
            askKbo: false,
        },
    },
    [legacyTopicReports.TrendingTopicsID]: {
        pendo: {
            addCard: "add-card-trending-topics",
        },
        dataAvailability: topicsDataAvailabilityPolicy,
        status: {
            tag: "Deprecated",
            infoText:
                "Drivers replaced Topics on 7/1/26. This report can only display Topics data prior to that date.",
            permission: "Drivers",
        },
        capabilities: {
            renderModes: ["#"],
            spotlight: false,
            kboAnalyze: false,
        },
        renderOptions: {
            menu: true,
            dataGridNumVisibleRows: 10,
        },
        pointSelectActions: {
            askKbo: false,
            viewInConvos: true,
            viewTopics: false,
            viewDrivers: false,
            viewData: true,
            viewLevel: false,
        },
    },
    [additionalReports.ExtractedConversationDataId]: {
        pendo: {
            addCard: "add-card-extracted-conversation-data",
        },
        capabilities: {
            renderModes: ["#"],
            spotlight: false,
            kboAnalyze: false,
            csvExport: "server-export",
        },
        renderOptions: {
            menu: true,
            dataGridNumVisibleRows: 10,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
    },
    [legacyTopicReports.TopicsOnTheRiseID]: {
        pendo: {
            addCard: "add-card-topics-on-the-rise",
        },
        dataAvailability: topicsDataAvailabilityPolicy,
        status: {
            tag: "Deprecated",
            infoText:
                "Drivers replaced Topics on 7/1/26. This report can only display Topics data prior to that date.",
            permission: "Drivers",
        },
        capabilities: {
            pointSelectMenu: true,
        },
        pointSelectActions: {
            askKbo: false,
            viewInConvos: true,
            viewTopics: false,
            viewDrivers: false,
            viewData: true,
            viewLevel: false,
        },
    },
    // Contacts
    [contacts.ContactTypePrevalenceId]: {
        pendo: {
            addCard: "add-card-contact-types",
        },
        // Endpoint can return grouping fields in both compare and grouped modes; strip only when neither applies.
        transforms: [stripGroupingWhenNotComparePrevOrGrouping],
        capabilities: {
            comparePrev: true,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.ContactType],
        },
    },
    [contacts.ContactTypePrevalenceAndEEId]: {
        pendo: {
            addCard: "add-card-contact-type-and-eddy-effect",
        },
        renderOptions: {
            palette: Palettes.specialEddyColors,
            forceStacked: true,
            legendVisibility: "bottom",
        },
        capabilities: {
            renderModes: ["#"],
        },
    },
    [contacts.ContactTypePrevalenceOverTimeId]: {
        pendo: {
            addCard: "add-card-contact-types-over-time",
        },
        renderOptions: {
            legendVisibility: "bottom",
            dataGridNumVisibleRows: 7,
            isTemporal: true,
        },
        dataGrid: {
            gridColumnType: "Contact Type",
            dataGridBuilder: buildContactTypesOverTimeGrid,
            selection: {
                behavior: "noRequest",
                onSelect: ({ chartStore, dataGridStore }) =>
                    dataGridStore.selectedRowIds.map((rowId) => {
                        const rowIdString = `${rowId}`;
                        return (
                            chartStore.optionsStore.customerTypes.find(
                                (type) => type.sourceName === rowIdString,
                            )?.name ?? rowIdString
                        );
                    }),
            },
        },
    },
    [contacts.EddyByContactTypeOverTimeId]: {
        pendo: {
            addCard: "add-card-eddy-effect-by-contact-type-over-time",
        },
        renderOptions: {
            // The % is out of all conversations that day, but the total sent is just this contact type
            hideEligibleConversations: true,
            legendVisibility: "bottom",
            dataGridNumVisibleRows: 7,
            isTemporal: true,
        },
        dataGrid: {
            gridColumnType: "Contact Type",
            dataGridBuilder: buildEddyByContactTypeOverTimeGrid,
            selection: {
                behavior: "noRequest",
                onSelect: ({ chartStore, dataGridStore }) =>
                    dataGridStore.selectedRowIds.map((rowId) => {
                        const rowIdString = `${rowId}`;
                        return (
                            chartStore.optionsStore.customerTypes.find(
                                (type) => type.sourceName === rowIdString,
                            )?.name ?? rowIdString
                        );
                    }),
            },
        },
    },

    // Additional Reports
    [additionalReports.ConversationsOverTimeId]: {
        pendo: {
            addCard: "add-card-conversations-over-time",
        },
        renderOptions: {
            isTemporal: true,
        },
        capabilities: {
            renderModes: ["#"],
            spotlight: false,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [],
        },
    },
    [additionalReports.CountByQuestionResponseId]: {
        pendo: {
            addCard: "add-card-question-response-count",
        },
        capabilities: {
            canSelectAllModules: true,
            csvExport: "server-export",
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [],
        },
        pointSelectActions: {
            viewInConvos: false,
            askKbo: false,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
        requestPatch: (req) => {
            const questionIds = Array.isArray(req.questionIds)
                ? req.questionIds.filter(
                      (id): id is string => typeof id === "string",
                  )
                : [];
            const nextQuestionId = questionIds[0];
            const hasQuestionId = typeof req.questionId === "string";
            if (!hasQuestionId && !!nextQuestionId) {
                const { questionIds: _questionIds, ...rest } = req;
                return {
                    ...rest,
                    questionId: nextQuestionId,
                };
            }
            return req;
        },
    },
    [additionalReports.JourneyStageAndEddyEffectId]: {
        pendo: {
            addCard: "add-card-patient-journey-stage-and-eddy-effect",
        },
        renderOptions: {
            forceStacked: true,
            patientJourneyBadge: true,
            palette: Palettes.specialEddyColors,
            comparePrevPalette: Palettes.specialEddyComparePrev,
            dataGridNumVisibleRows: 6,
        },
        capabilities: {
            renderModes: ["#"],
        },
    },
    [additionalReports.JourneyStageCountId]: {
        pendo: {
            addCard: "add-card-patient-journey-stages",
        },
        // Endpoint can return grouping fields in both compare and grouped modes; strip only when neither applies.
        transforms: [stripGroupingWhenNotComparePrevOrGrouping],
        renderOptions: {
            patientJourneyBadge: true,
            dataGridNumVisibleRows: 6,
        },
        capabilities: {
            comparePrev: true,
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [GroupByOption.PatientJourney],
        },
    },
    [additionalReports.ModelResultsCountId]: {
        pendo: {
            addCard: "add-card-model-results-count",
        },
        capabilities: {
            spotlight: false,
            kboAnalyze: true,
            csvExport: "server-export",
        },
        grouping: {
            enabled: true,
        },
        pagination: {
            paginated: true,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
    },
    [additionalReports.ModelResultsOverTimeId]: {
        pendo: {
            addCard: "add-card-model-results-over-time",
        },
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
        },
        capabilities: {
            csvExport: "server-export",
            kboAnalyze: true,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
    },
    [additionalReports.ModuleResultsCountId]: {
        pendo: {
            addCard: "add-card-module-results-count",
        },
        renderOptions: {
            forceStacked: true,
        },
        capabilities: {
            canSelectAllModules: true,
            spotlight: false,
            kboAnalyze: false,
            csvExport: "server-export",
        },
        pagination: {
            paginated: true,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
        pointSelectActions: {
            viewInConvos: false,
        },
    },
    [additionalReports.PublishedModelResultsCountId]: {
        capabilities: {
            spotlight: false,
            kboAnalyze: true,
            csvExport: "server-export",
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [],
        },
        pagination: {
            paginated: true,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
    },
    [additionalReports.GroupedModelResultsId]: {
        pendo: {
            addCard: "add-card-grouped-model-results",
        },
        renderOptions: {
            // One stacked bar per model; segments are the model's result categories.
            forceStacked: true,
        },
        // Order the result-category stacks via the standard "results" order rule (bars use the
        // chart's own order rule).
        stackOrderRuleIdentifier: "results",
        capabilities: {
            spotlight: false,
            kboAnalyze: true,
            csvExport: "server-export",
        },
        pagination: {
            paginated: true,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
        requestPatch: (req) => ({
            ...req,
            classifierGroupId: req.classifierGroupId,
        }),
        // KBo card's "...for {model}" selector: the group's analyzable classifiers (group id comes from
        // selectedIds.classifierGroup for the interactive chart).
        kboModelOptions: {
            loadOptions: loadGroupClassifierOptions,
        },
    },
    [additionalReports.PublishedGroupedModelResultsId]: {
        renderOptions: {
            // One stacked bar per model; segments are the model's result categories.
            forceStacked: true,
        },
        // Order the result-category stacks via the standard "results" order rule (bars use the
        // chart's own order rule).
        stackOrderRuleIdentifier: "results",
        // Let internal users drag-reorder the bars (one per published model in the group). The bar
        // order is the chart's own "Models" order rule; labels come from the group's classifiers,
        // rolled up across sub-groups to match the bars the chart renders.
        reorder: {
            label: "Reorder Chart Bars",
            loadOptions: ({ dataEndpoint }) => {
                const groupId = parseClassifierGroupId(dataEndpoint);
                return groupId
                    ? classifierService.getClassifiersInGroup(
                          groupId,
                          undefined,
                          true,
                      )
                    : Promise.resolve([]);
            },
        },
        capabilities: {
            spotlight: false,
            kboAnalyze: true,
            csvExport: "server-export",
        },
        pagination: {
            paginated: true,
        },
        exportConfig: {
            allowColumnSelection: true,
        },
        // No requestPatch: the published chart's classifierGroupId travels in the endpoint URL
        // (?classifierGroupId=) and is read server-side, unlike the interactive GroupedModelResults chart.
        // KBo card's "...for {model}" selector: the group's analyzable classifiers (group id parsed from
        // the endpoint URL).
        kboModelOptions: {
            loadOptions: loadGroupClassifierOptions,
        },
    },
    [additionalReports.PublishedModelResultsOverTimeId]: {
        renderOptions: {
            legendVisibility: "bottom",
            isTemporal: true,
            emptyStateVariant: "modelResultsEmpty",
        },
        capabilities: {
            kboAnalyze: true,
            csvExport: "server-export",
        },
        exportConfig: {
            allowColumnSelection: true,
        },
    },
    [additionalReports.CallDirectionId]: {
        pendo: {
            addCard: "add-card-call-direction",
        },
    },
    [additionalReports.LillySafetyEventTypesId]: {
        pendo: {
            addCard: "add-card-lilly-safety-event-types",
        },
        capabilities: {
            pointSelectMenu: false,
        },
    },
    [additionalReports.AdverseEventsModelPerformanceId]: {
        pendo: {
            addCard: "add-card-adverse-events-model-performance",
        },
        dataGrid: {
            gridColumnType: "Model Performance",
            dataGridBuilder: buildModelPerformanceGrid,
        },
        capabilities: {
            csvExport: "client-csv",
            pointSelectMenu: false,
            spotlight: false,
        },
        renderOptions: {
            showAccuracy: true,
        },
    },
    [additionalReports.SafetyEventsModelPerformanceId]: {
        pendo: {
            addCard: "add-card-safety-events-model-performance",
        },
        dataGrid: {
            gridColumnType: "Model Performance",
            dataGridBuilder: buildModelPerformanceGrid,
        },
        capabilities: {
            csvExport: "client-csv",
            pointSelectMenu: false,
            spotlight: false,
        },
        renderOptions: {
            showAccuracy: true,
        },
    },
    [additionalReports.ProductComplaintsModelPerformanceId]: {
        pendo: {
            addCard: "add-card-product-complaints-model-performance",
        },
        dataGrid: {
            gridColumnType: "Model Performance",
            dataGridBuilder: buildModelPerformanceGrid,
        },
        capabilities: {
            csvExport: "client-csv",
            pointSelectMenu: false,
            spotlight: false,
        },
        renderOptions: {
            showAccuracy: true,
        },
    },
    [additionalReports.SpecialSituationsModelPerformanceId]: {
        pendo: {
            addCard: "add-card-special-situations-model-performance",
        },
        dataGrid: {
            gridColumnType: "Model Performance",
            dataGridBuilder: buildModelPerformanceGrid,
        },
        capabilities: {
            csvExport: "client-csv",
            pointSelectMenu: false,
            spotlight: false,
        },
        renderOptions: {
            showAccuracy: true,
        },
    },
    [additionalReports.TotalConversationsBarId]: {
        pendo: {
            addCard: "add-card-total-conversations-bar",
        },
        grouping: {
            enabled: true,
            disabledGroupByNames: [],
        },
        pagination: {
            paginated: true,
        },
    },
    [additionalReports.TotalConversationsDonutId]: {
        pendo: {
            addCard: "add-card-total-conversations-donut",
        },
        grouping: {
            enabled: true,
            ignoreGroupingOrderRule: true,
            disabledGroupByNames: [],
        },
        pagination: {
            paginated: true,
        },
    },
};

export const emptyEndpointConfig: EndpointBehaviorConfig = {
    requestPatch: (req) => req,
    transforms: [],
    dataGrid: undefined,
    pagination: {
        paginated: false,
    },
    capabilities: {
        spotlight: false,
        kboAnalyze: false,
        sqlView: false,
        pngExport: false,
        csvExport: "none",
        canSelectAllModules: false,
        pointSelectMenu: false,
        comparePrev: false,
        renderModes: [],
        objectFilters: false,
    },
    renderOptions: {
        palette: undefined,
        comparePrevPalette: undefined,
        forceStacked: false,
        legendVisibility: "none",
        isTemporal: false,
        patientJourneyBadge: false,
        menu: false,
        showAccuracy: false,
        emptyStateVariant: "empty",
        preferSeriesInTooltip: false,
        dataGridNumVisibleRows: 5,
        cacheKeyStrategy: "filtersOrSelectionsChange",
    },
    grouping: {
        enabled: false,
        ignoreGroupingOrderRule: false,
        disabledGroupByNames: [],
    },
    exportConfig: {
        allowColumnSelection: false,
    },
    pointSelectActions: {
        askKbo: false,
        viewInConvos: false,
        viewTopics: false,
        viewDrivers: false,
        viewData: false,
        viewLevel: false,
    },
    pointSelectActionsByZoomLevel: {},
    percentRemainderSeries: false,
    status: undefined,
};

export function getEndpointBehaviorConfig({
    dataEndpoint,
    selectionType,
}: {
    dataEndpoint: SignalDataEndpoint | string;
    selectionType?: ISignalsSelectionTypes;
}): EndpointBehaviorConfig {
    void selectionType;

    const normalizeEndpoint = (
        endpoint: SignalDataEndpoint | string,
    ): SignalDataEndpoint | string => {
        if (
            endpoint.includes(additionalReports.PublishedGroupedModelResultsId)
        ) {
            // Published grouped charts carry ?classifierGroupId= in the endpoint; normalize to the base id.
            return additionalReports.PublishedGroupedModelResultsId;
        }
        if (
            endpoint.includes(additionalReports.PublishedModelResultsOverTimeId)
        ) {
            return additionalReports.PublishedModelResultsOverTimeId;
        }
        if (endpoint.includes(additionalReports.PublishedModelResultsCountId)) {
            return additionalReports.PublishedModelResultsCountId;
        }
        if (endpoint.includes(additionalReports.ModelResultsOverTimeId)) {
            return additionalReports.ModelResultsOverTimeId;
        }
        if (endpoint.includes(additionalReports.ModelResultsCountId)) {
            return additionalReports.ModelResultsCountId;
        }
        return endpoint;
    };

    const normalizedEndpoint = normalizeEndpoint(dataEndpoint);
    const overrides =
        endpointBehaviorConfigs[normalizedEndpoint as SignalDataEndpoint];

    const drilldown =
        overrides?.drilldown ??
        drilldownStrategies[normalizedEndpoint as SignalDataEndpoint];

    const config = mergeWithDefaults({ ...overrides, drilldown });

    // A temporal chart already plots a date on x, so grouping it by date would put the same
    // dimension on both axes. Derived rather than listed per endpoint so the next temporal chart
    // someone adds opts out by being temporal.
    if (
        config.renderOptions.isTemporal &&
        !config.grouping.disabledGroupByNames.includes(GroupByOption.Date)
    ) {
        config.grouping.disabledGroupByNames = [
            ...config.grouping.disabledGroupByNames,
            GroupByOption.Date,
        ];
    }

    return config;
}
